import { ExternalLink } from 'lucide-react';
import { Button } from '../components/ui';
import { useT } from '../i18n';

export function OutsideTelegram() {
  const t = useT();
  return (
    <div
      className="fade-in"
      style={{
        position: 'relative',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--tg-bg)',
        padding: 'calc(var(--safe-top) + 28px) 22px calc(var(--safe-bottom) + 28px)',
        overflow: 'hidden',
      }}
    >
      {/* Ambient brand glow */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: -120,
          right: -100,
          width: 360,
          height: 360,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(7,141,238,0.18) 0%, rgba(7,141,238,0) 65%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          top: 120,
          left: -120,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background:
            'radial-gradient(circle, rgba(7,141,238,0.10) 0%, rgba(7,141,238,0) 70%)',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />

      {/* Hero */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <img
          src="/neburst-logo.svg"
          alt="Neburst"
          style={{
            display: 'block',
            width: 'min(220px, 60vw)',
            height: 'auto',
            marginBottom: 14,
          }}
        />
        <span
          style={{
            display: 'inline-block',
            fontSize: 11,
            fontWeight: 600,
            letterSpacing: '0.14em',
            textTransform: 'uppercase',
            color: 'var(--tg-subtitle)',
            marginBottom: 32,
          }}
        >
          Console · Telegram
        </span>

        <h1
          style={{
            margin: 0,
            fontSize: 30,
            fontWeight: 700,
            letterSpacing: '-0.025em',
            lineHeight: 1.15,
            color: 'var(--tg-text)',
          }}
        >
          {t('guard.title')}
        </h1>
        <p
          style={{
            margin: '12px 0 28px',
            color: 'var(--tg-subtitle)',
            fontSize: 15,
            lineHeight: 1.55,
            maxWidth: 380,
          }}
        >
          {t('guard.desc')}
        </p>
      </div>

      {/* Steps card — custom layout, larger numbered pills with brand accent */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <h2
          style={{
            margin: '0 4px 12px',
            fontSize: 11.5,
            fontWeight: 600,
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            color: 'var(--tg-section-header-text)',
          }}
        >
          {t('guard.howTitle')}
        </h2>
        <div
          style={{
            background: 'var(--tg-section-bg)',
            borderRadius: 'var(--r-lg)',
            padding: '6px 4px',
            boxShadow: 'var(--sh-card)',
          }}
        >
          <Step n={1} text={t('guard.step1')} />
          <Step n={2} text={t('guard.step2')} />
          <Step n={3} text={t('guard.step3')} last />
        </div>
      </div>

      {/* CTA */}
      <div style={{ position: 'relative', zIndex: 1, marginTop: 24 }}>
        <Button
          leading={<ExternalLink size={16} />}
          size="lg"
          onClick={() => window.open('https://t.me/neburst_dev_bot', '_blank', 'noopener')}
        >
          {t('guard.openBot')}
        </Button>
        <p
          style={{
            margin: '14px 0 0',
            fontSize: 12,
            textAlign: 'center',
            color: 'var(--tg-hint)',
            letterSpacing: '-0.005em',
          }}
        >
          tgapp.neburst.dev
        </p>
      </div>

      {/* Spacer to push CTA to bottom on tall screens */}
      <div style={{ flex: 1 }} />
    </div>
  );
}

function Step({ n, text, last }: { n: number; text: string; last?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '14px 16px',
        borderBottom: last ? 'none' : '0.5px solid var(--tg-separator)',
      }}
    >
      <div
        style={{
          width: 30,
          height: 30,
          borderRadius: '50%',
          background: 'var(--brand-soft)',
          color: 'var(--brand)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 14,
          fontWeight: 700,
          letterSpacing: '-0.02em',
          flexShrink: 0,
          border: '1px solid rgba(7,141,238,0.18)',
        }}
      >
        {n}
      </div>
      <div
        style={{
          fontSize: 14.5,
          fontWeight: 500,
          letterSpacing: '-0.005em',
          color: 'var(--tg-text)',
          lineHeight: 1.4,
        }}
      >
        {text}
      </div>
    </div>
  );
}
